import React, {ReactNode, useContext, useEffect} from 'react';
import {StoreContext} from '../base/root-store';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    IChatAnswerProps,
    IChatMessageProps,
    IChatReplyFormProps,
    Main,
    Notification,
    TChatItem,
    TemplateChat, TProgressColor,
} from '../../../patterns';
import {formatToDayMonthAndYear} from '../base/dateUtils';
import {
    ChatMessage,
    ChatVariables,
    ChatViewStore,
    FiveChoiceAction,
    FreeTextAction,
    MultipleChoiceAction,
} from './chat-view-store';
import {ErrorBoundary} from '../base/ErrorBoundary';
import {GenericError} from '../base/GenericError';
import {IChatFiveOptionsReplyProps} from '../../../patterns/components/chat/chat';

export interface IChatBaseProps {
    children: ReactNode;
}

export const ChatBase = (props: IChatBaseProps): JSX.Element => (
    <ErrorBoundary>
        <Main showBackground={true}>
            {props.children}
        </Main>
    </ErrorBoundary>
);

export const ChatPage = observer((props: {loadContext?: 'history'}): JSX.Element => {
    const store = useContext(StoreContext).chatViewStore;

    useEffect(() => {
        if (props.loadContext === 'history') {
            store.onEnter();
        }

        return () => {
            store.variables = new ChatVariables();
        };
    }, [props.loadContext, store]);

    if (store.state === 'error' && store.errorReason === 'generic') {
        return <GenericError/>;
    }
    if (store.state === 'error' && store.errorReason === 'user-deleted') {
        return <Notification
            text={store.label('common.user.deleted.error-message')}
            defaultIsOpen={true}
            type="info"
        />;
    }
    if (store.state === 'loading') {
        return <TemplateChat chat={{
            isLoading: true,
        }}/>;
    }
    if (store.variables.expired) {
        return <Notification
            title={store.label('chat.expired.title')}
            text={store.label('chat.expired.description')}
            defaultIsOpen={true}
            type="info"
        />;
    }

    const items: TChatItem[] = store.variables.deliveredMessages
        .map((message) => message.party === 'bot' ? messageProps(store, message) : answerProps(message));

    return <TemplateChat chat={{
        items,
        isLoading: store.botTyping,
        reply: replyProps(store),
    }}/>;
});

const messageProps = (store: ChatViewStore, message: ChatMessage): IChatMessageProps => ({
    element: 'message',
    message: message.text,
    hasClickableUrl: message.hasClickableUrl,
    time: {
        iso: message.date,
        value: formatToDayMonthAndYear(message.date),
    },
    results: message.questionStats && message.questionStats.answered > 2 ? [
        {
            percent: message.questionStats.participationPercentage,
            label: store.label('chat.question.activity', {
                number_of_answers: message.questionStats.answered.toString(),
                number_of_asked: message.questionStats.prepared.toString()}),
            icon: 'users',
            color: 'blue',
        },
        ...(!message.questionStats.onlyFreeTextAnswers ? [{
            percent: message.questionStats.scorePercentage,
            label: `${message.questionStats.scorePercentage}%`,
            icon: 'like',
            color: message.questionStats.scorePercentage > 50 ? 'green' : 'red' as TProgressColor,
        }] : []),
    ] : undefined,
});

const answerProps = (message: ChatMessage): IChatAnswerProps => ({
    element: 'answer',
    text: message.text,
    color: message.color === 'white' ? undefined : message.color,
    time: {
        iso: message.date,
        value: formatToDayMonthAndYear(message.date),
    },
});

const replyProps = (store: ChatViewStore): IButtonProps[] | IChatReplyFormProps | IChatFiveOptionsReplyProps | undefined => {
    if (store.botTyping) {
        return undefined;
    }

    const { questions, chatActionPayload } = store.payload!;
    const initialComment = questions.length && chatActionPayload.answerContext !== 'ONLY_FREETEXT' ? questions[questions.length - 1].comment : undefined;
    const action = store.variables.action;

    if (action === undefined) {
        return undefined;
    }

    if (action instanceof MultipleChoiceAction) {
        return action.choices.map((choice) => ({
            text: choice.text,
            color: choice.color,
            onClick: (): void => store.onSubmitChoice(choice.value, choice.questionAnswerContext),
        }));
    }

    if (action instanceof FiveChoiceAction) {
        return {
            hasFiveOptions: true,
            actions: action.choices.map((choice) => ({
                text: choice.text,
                color: choice.color,
                onClick: (): void => store.onSubmitAnswer(choice.value, choice.questionAnswerContext),
            }))};
    }

    if (action instanceof FreeTextAction) {
        return {
            textField: {
                id: 'chat-reply-from',
                name: 'chat-reply-from',
                defaultValue: initialComment !== undefined ? initialComment : '',
                label: '',
                onChange: (value): void => store.onFreeTextChange(value),
            },
            actions: action.choices.map((choice) =>({
                text: choice.text,
                color: choice.color,
                onClick: (): void => store.onSubmitFreeTextChoice(choice.value),
            })),
        } as IChatReplyFormProps;
    }
};
